/**
 * COMMISSION RECORD CREATOR
 * 
 * Specialized component for creating commission transaction records
 * with user and purchase selection dropdowns
 */

import { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CommissionRecordCreatorProps {
  referrerId: number;
  onClose: () => void;
  onCreate: (newRecord: any) => void;
}

interface User {
  id: number;
  username: string;
  email: string;
}

interface Purchase {
  id: string;
  user_id: number;
  total_amount: number;
  shares_purchased: number;
  created_at: string;
}

export function CommissionRecordCreator({
  referrerId,
  onClose,
  onCreate
}: CommissionRecordCreatorProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  
  const [newRecord, setNewRecord] = useState<Record<string, any>>({
    referrer_id: referrerId,
    referred_id: '',
    share_purchase_id: '',
    commission_rate: 15.00,
    share_purchase_amount: 0,
    usdt_commission: 0,
    share_commission: 0,
    status: 'approved',
    payment_date: new Date().toISOString().slice(0, 16)
  });
  
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load all users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Load purchases when referred_id changes
  useEffect(() => {
    if (newRecord.referred_id) {
      loadPurchases(newRecord.referred_id);
    } else {
      setPurchases([]);
    }
  }, [newRecord.referred_id]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      // Fetch users with pagination via API
      const batchSize = 1000;
      let allUsers: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ operation: 'load_users_paginated', data: { offset, limit: batchSize } })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error);

        const batch = result.data || [];
        if (batch.length > 0) {
          allUsers = [...allUsers, ...batch];
          offset += batchSize;
          hasMore = batch.length === batchSize;
        } else {
          hasMore = false;
        }
      }

      setUsers(allUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadPurchases = async (userId: number) => {
    try {
      setLoadingPurchases(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/commission-operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_user_purchases', data: { userId } })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setPurchases(result.data || []);
    } catch (err) {
      console.error('Error loading purchases:', err);
      setError('Failed to load purchases');
    } finally {
      setLoadingPurchases(false);
    }
  };

  const handlePurchaseChange = (purchaseId: string) => {
    const purchase = purchases.find(p => p.id === purchaseId);
    if (purchase) {
      setNewRecord({
        ...newRecord,
        share_purchase_id: purchaseId,
        share_purchase_amount: purchase.total_amount
      });
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');
      setSuccess('');

      // Validate required fields
      if (!newRecord.referred_id || !newRecord.share_purchase_id) {
        throw new Error('Please select both a user and a purchase');
      }

      // Validate that we have a valid admin user id for audit logging
      const adminUserId = typeof (window as any).currentUserId === 'number' ? (window as any).currentUserId : null;
      if (!adminUserId) {
        throw new Error('Current admin user is not available. Please re-open the admin dashboard and try again.');
      }

      const response = await fetch('/api/admin/manual-record-editor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'CREATE',
          table: 'commission_transactions',
          record: newRecord,
          userId: adminUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create record');
      }

      const data = await response.json();
      setSuccess('✅ Commission record created successfully');
      onCreate(data.data);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#1F2937',
        border: '1px solid #374151',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#FFFFFF', margin: 0 }}>
            ➕ Add New Commission Record
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0
            }}
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            backgroundColor: '#7F1D1D',
            color: '#FCA5A5',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#1F3A1F',
            color: '#86EFAC',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        {/* Form Fields */}
        <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Referred User Dropdown */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              Referred User (Who Made Purchase) *
            </label>
            <select
              value={newRecord.referred_id}
              onChange={(e) => setNewRecord({ ...newRecord, referred_id: parseInt(e.target.value) || '' })}
              disabled={loadingUsers}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box',
                cursor: loadingUsers ? 'not-allowed' : 'pointer',
                opacity: loadingUsers ? 0.5 : 1
              }}
            >
              <option value="">Select a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  @{user.username} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Share Purchase Dropdown */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              Share Purchase *
            </label>
            <select
              value={newRecord.share_purchase_id}
              onChange={(e) => handlePurchaseChange(e.target.value)}
              disabled={!newRecord.referred_id || loadingPurchases}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box',
                cursor: (!newRecord.referred_id || loadingPurchases) ? 'not-allowed' : 'pointer',
                opacity: (!newRecord.referred_id || loadingPurchases) ? 0.5 : 1
              }}
            >
              <option value="">Select a purchase...</option>
              {purchases.map(purchase => (
                <option key={purchase.id} value={purchase.id}>
                  ${purchase.total_amount.toFixed(2)} - {purchase.shares_purchased} shares ({new Date(purchase.created_at).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          {/* Commission Rate */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              Commission Rate (%) *
            </label>
            <input
              type="number"
              step="0.01"
              value={newRecord.commission_rate ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setNewRecord({ ...newRecord, commission_rate: '' });
                  return;
                }
                const parsed = Number.parseFloat(raw);
                setNewRecord({ ...newRecord, commission_rate: Number.isFinite(parsed) ? parsed : '' });
              }}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* USDT Commission */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              USDT Commission *
            </label>
            <input
              type="number"
              step="0.01"
              value={newRecord.usdt_commission ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setNewRecord({ ...newRecord, usdt_commission: '' });
                  return;
                }
                const parsed = Number.parseFloat(raw);
                setNewRecord({ ...newRecord, usdt_commission: Number.isFinite(parsed) ? parsed : '' });
              }}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Share Commission */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              Share Commission *
            </label>
            <input
              type="number"
              step="0.01"
              value={newRecord.share_commission ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setNewRecord({ ...newRecord, share_commission: '' });
                  return;
                }
                const parsed = Number.parseFloat(raw);
                setNewRecord({ ...newRecord, share_commission: Number.isFinite(parsed) ? parsed : '' });
              }}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Status */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              Status *
            </label>
            <select
              value={newRecord.status}
              onChange={(e) => setNewRecord({ ...newRecord, status: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            >
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Payment Date */}
          <div>
            <label style={{
              display: 'block',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase'
            }}>
              Payment Date *
            </label>
            <input
              type="datetime-local"
              value={newRecord.payment_date}
              onChange={(e) => setNewRecord({ ...newRecord, payment_date: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={creating}
            style={{
              padding: '10px 20px',
              backgroundColor: '#374151',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: creating ? 0.5 : 1
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleCreate}
            disabled={creating || !newRecord.referred_id || !newRecord.share_purchase_id}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: (creating || !newRecord.referred_id || !newRecord.share_purchase_id) ? 0.5 : 1
            }}
          >
            {creating ? '⏳ Creating...' : '➕ Create Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

